import { useState } from 'react';

import {
  BANDS_DJS_TAXONOMY_KEY,
  CAKES_DESSERTS_TAXONOMY_KEY,
  CATERING_TAXONOMY_KEY,
  FLORISTS_TAXONOMY_KEY,
  HAIR_MAKEUP_TAXONOMY_KEY,
  PHOTOGRAPHERS_TAXONOMY_KEY,
  PLANNERS_TAXONOMY_KEY,
  VENUES_TAXONOMY_KEY,
  VIDEOGRAPHERS_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomyKeyFromId } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import { getVendorWordSingular } from '@zola-helpers/client/dist/es/marketplace/vendorWords';
import { CtaType } from '@zola-helpers/client/dist/es/tracking/ctaEvents/ctaEvents';
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';

import cx from 'classnames';
import arrayMutators from 'final-form-arrays';
import _get from 'lodash/get';
import { Form } from 'react-final-form';

import {
  UpdateAccountVendorPreferenceRequest,
  SaveAccountWeddingPreferencesRequest,
  CreateAddressRequest,
  AccountWeddingPreferencesView,
} from '~/types/responseTypes';
import { formatDateIso, formatDateUtc } from '~/util/dateUtils';
import { getMutator, setField, clearField } from '~/util/formMutators';
import { TrackingProductLocation, trackCtaClicked } from '~/util/trackingHelper';

import { useSearchResultsPageContext } from '../../contexts/SearchResultsPageContext';
import { FloristQuestions } from './FloristQuestions';
import { GeneralQuestions } from './GeneralQuestions';
import { PhotographerQuestions } from './PhotographerQuestions';
import { QuestionBlock } from './QuestionBlock';
import { VenueQuestions } from './VenueQuestions';
import { WeddingQuestions, WeddingLocationFormValues } from './WeddingQuestions';

import styles from './vendorDrawer.module.less';

const vendorQuestionsMap: { [key: string]: React.ReactChild } = {
  [PHOTOGRAPHERS_TAXONOMY_KEY]: <PhotographerQuestions category={PHOTOGRAPHERS_TAXONOMY_KEY} />,
  [FLORISTS_TAXONOMY_KEY]: <FloristQuestions />,
  [VENUES_TAXONOMY_KEY]: <VenueQuestions />,
  [VIDEOGRAPHERS_TAXONOMY_KEY]: <PhotographerQuestions category={VIDEOGRAPHERS_TAXONOMY_KEY} />,
  [BANDS_DJS_TAXONOMY_KEY]: <GeneralQuestions />,
  [HAIR_MAKEUP_TAXONOMY_KEY]: <GeneralQuestions />,
  [CATERING_TAXONOMY_KEY]: <GeneralQuestions />,
  [CAKES_DESSERTS_TAXONOMY_KEY]: <GeneralQuestions />,
  [PLANNERS_TAXONOMY_KEY]: <GeneralQuestions />,
};

const trackCtaData = (btn: string, ctaType: CtaType) => {
  trackCtaClicked({
    location: TrackingProductLocation.TOP_PICKS_DRAWER,
    section: 'TOP_PICKS_DRAWER_FORM',
    ctaId: btn,
    ctaType,
  });
};

interface VendorEditFormProps {
  setShowEditForm: (key: boolean) => void;
  vendorTypeKey: string;
  taxonomyId: number | null;
}

interface SubmissionProps extends WeddingLocationFormValues {
  taxonomyNodeId?: number | null;
  photographerBudget?: number | null;
  weddingBudget?: number | null;
  eventDate?: string | null;
  eventDateYearMonth?: string | null;
  userObjectId?: string | null;
  serviceEndPriceCents: number;
  optionFacetKeys: string[];
  flexibleOnPrice: boolean;
  overallStyle?: string[] | null;
  weddingDate?: string | null;
  flexibleOnDate?: boolean | null;
}

export const VendorDrawerEditForm = ({
  setShowEditForm,
  vendorTypeKey,
  taxonomyId,
}: VendorEditFormProps): JSX.Element => {
  const {
    updateWeddingPreferences,
    updateVendorPreferences,
    weddingPreferences,
    vendorPreferences,
  } = useSearchResultsPageContext() as ReturnType<typeof useSearchResultsPageContext> & {
    weddingPreferences: AccountWeddingPreferencesView;
  };

  const vendorPref = vendorPreferences.find((pref) => pref.taxonomyNodeId === taxonomyId);

  const [newMarket, setNewMarket] = useState<number | null>(null);

  const heading = taxonomyId
    ? `Your ${getVendorWordSingular(vendorTypeKey)} Preferences`
    : 'Your Wedding Details';

  const dek = taxonomyId
    ? 'We’ll recommend vendors who fit what you’re looking for.'
    : 'We’ll recommend vendors who can deliver your unique vision.';

  const handleOptionFacetKeys = (values: SubmissionProps) => {
    const keys = _get(values, 'optionFacetKeys', []);
    if (taxonomyId && getVendorTaxonomyKeyFromId(taxonomyId) === PHOTOGRAPHERS_TAXONOMY_KEY) {
      // if we're editing photographer preferences, we need to combine the
      // user's selections from two different fields
      const results = keys?.filter((key) => key.startsWith('personality'));
      return results?.concat(_get(values, 'photographyStyle', []));
    }
    return keys;
  };

  const handleWeddingSubmit = (values: SubmissionProps) => {
    const { uuid } = weddingPreferences;
    /** addresses with null fields will cause 500 error, so avoid sending those */
    let address: CreateAddressRequest | undefined = {
      city: _get(values, 'weddingLocation.city', weddingPreferences.address?.city),
      stateProvince: _get(
        values,
        'weddingLocation.stateProvince',
        weddingPreferences.address?.stateProvince
      ),
      address1: null,
      address2: null,
      postalCode: null,
      countryCode: null,
      latitude: null,
      longitude: null,
      googleMapsPlaceId: null,
    };
    if (Object.values(address).filter((f) => f !== null).length === 0) {
      address = undefined;
    }

    const submission: SaveAccountWeddingPreferencesRequest = {
      ...(address && { address }),
      eventDate: values.weddingDate
        ? formatDateIso(values.weddingDate)
        : weddingPreferences.eventDate,
      eventDateYearMonth: values.weddingDate
        ? formatDateIso(values.weddingDate)
        : weddingPreferences.eventDateYearMonth,
      weddingOptionFacetKeys:
        _get(
          values,
          'overallStyle',
          weddingPreferences.facetsView?.map((facet) => facet.key)
        ) || null,
      guestCount: parseInt(_get(values, 'guestCount', weddingPreferences.guestCount), 10),
      flexibleOnDate: _get(values, 'flexibleOnDate', weddingPreferences.flexibleOnDate) as boolean,
      flexibleOnGuestCount: _get(
        values,
        'flexibleOnGuestCount',
        weddingPreferences.flexibleOnGuestCount
      ),
      vendorMarketId: newMarket || weddingPreferences.vendorMarketId,
      // values that cannot be changed in this form
      userObjectId: weddingPreferences.userObjectId,
      firstName: weddingPreferences.firstName,
      lastName: weddingPreferences.lastName,
      partnerFirstName: weddingPreferences.partnerFirstName,
      partnerLastName: weddingPreferences.partnerLastName,
      weddingEndPriceCents: weddingPreferences.weddingEndPriceCents,
      flexibleOnLocation: weddingPreferences.flexibleOnLocation,
      flexibleOnPrice: false,
    };
    updateWeddingPreferences(uuid, submission);
    setShowEditForm(false);
  };

  const handleVendorSubmit = (values: SubmissionProps) => {
    if (vendorPreferences) {
      const { uuid = null } = vendorPref || {};
      const submission: UpdateAccountVendorPreferenceRequest = {
        taxonomyNodeId: taxonomyId,
        userObjectId: vendorPref?.userObjectId,
        serviceEndPriceCents: values.serviceEndPriceCents * 100,
        optionFacetKeys: handleOptionFacetKeys(values),
        flexibleOnPrice: values.flexibleOnPrice,
      };
      updateVendorPreferences(uuid, submission);
      if (taxonomyId && getVendorTaxonomyKeyFromId(taxonomyId) === VENUES_TAXONOMY_KEY) {
        // if it's a venue, save wedding preferences too
        handleWeddingSubmit(values);
      } else {
        setShowEditForm(false);
      }
    }
  };

  return (
    <div>
      <LinkV2
        className={styles.back}
        onClick={() => {
          setShowEditForm(false);
          trackCtaData('Back to All Preferences', CtaType.LINK);
        }}
      >
        Back to All Preferences
      </LinkV2>
      <Form
        mutators={{
          ...arrayMutators,
          setField: getMutator<SubmissionProps>(setField),
          clearField: getMutator<SubmissionProps>(clearField),
        }}
        initialValues={{
          weddingDate: weddingPreferences.eventDate
            ? formatDateUtc(weddingPreferences.eventDate, 'M/DD/YYYY')
            : '',
        }}
        onSubmit={vendorTypeKey === 'wedding-general' ? handleWeddingSubmit : handleVendorSubmit}
        render={({ form, handleSubmit }) => {
          return (
            <form className={styles.editForm} onSubmit={handleSubmit}>
              <div className={styles.questionFields}>
                <h3 className={styles.heading}>{heading}</h3>
                <div className={styles.dek}>{dek}</div>
                {vendorQuestionsMap[vendorTypeKey] && vendorPref ? (
                  <QuestionBlock
                    form={form}
                    weddingPreferences={weddingPreferences}
                    vendorPreference={vendorPref}
                    activeVendorType={vendorTypeKey}
                  >
                    {vendorQuestionsMap[vendorTypeKey]}
                  </QuestionBlock>
                ) : (
                  <WeddingQuestions
                    form={form}
                    weddingPreferences={weddingPreferences}
                    setNewMarket={setNewMarket}
                  />
                )}
              </div>
              <div className={cx('side-drawer__footer', styles.footer)}>
                <LinkV2
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    setShowEditForm(false);
                    trackCtaData('Back', CtaType.LINK);
                  }}
                  className="mr-secondary"
                >
                  Back
                </LinkV2>
                <ButtonV2 onClick={() => trackCtaData('Save', CtaType.BUTTON)} type="submit">
                  Save
                </ButtonV2>
              </div>
            </form>
          );
        }}
      />
    </div>
  );
};
